import type { GnomeSpineEmote } from "./gnomeSpineBody.ts";

/** Presentation choices only; none of these rolls changes a gameplay reward. */
export const GNOME_EMOTE_OCCASIONS = [
    "quest",
    "quest-special",
    "track",
    "job",
    "tap",
    "greeting",
    "environment",
    "outfit",
    "item",
    "ambient",
] as const;
export type GnomeEmoteOccasion = (typeof GNOME_EMOTE_OCCASIONS)[number];

interface WeightedEmote {
    emote: GnomeSpineEmote;
    weight: number;
}

/** Job reactions have an open stage. Counter scenes use readable upright poses. */
export const GNOME_EMOTE_POOLS = {
    quest: [
        { emote: "animations/emotes/nailed_it", weight: 7 },
        { emote: "animations/emotes/happy_hop", weight: 3 },
        { emote: "animations/emotes/victory_pump", weight: 1 },
    ],
    "quest-special": [
        { emote: "animations/emotes/nailed_it", weight: 3 },
        { emote: "animations/emotes/happy_hop", weight: 3 },
        { emote: "animations/emotes/victory_pump", weight: 4 },
    ],
    track: [
        { emote: "animations/emotes/mastered_it", weight: 5 },
        { emote: "animations/emotes/ta_da", weight: 3 },
        { emote: "animations/emotes/victory_pump", weight: 2 },
        { emote: "animations/emotes/very_important_bow", weight: 2 },
    ],
    job: [
        { emote: "animations/emotes/me_promoted", weight: 5 },
        { emote: "animations/emotes/sideflip_jump", weight: 2 },
        { emote: "animations/emotes/heel_click_hooray", weight: 2 },
        { emote: "animations/emotes/disco_gnome", weight: 2 },
        { emote: "animations/emotes/pebbler_groove", weight: 2 },
    ],
    tap: [
        { emote: "animations/emotes/who_me", weight: 5 },
        { emote: "animations/emotes/awkward", weight: 3 },
        { emote: "animations/emotes/ta_da", weight: 2 },
        { emote: "animations/emotes/very_important_bow", weight: 2 },
    ],
    greeting: [
        { emote: "animations/emotes/overeager_hello", weight: 5 },
        { emote: "animations/emotes/very_important_bow", weight: 1 },
        { emote: "animations/emotes/ta_da", weight: 1 },
    ],
    environment: [
        { emote: "animations/emotes/onwards", weight: 5 },
        { emote: "animations/emotes/ta_da", weight: 2 },
        { emote: "animations/emotes/very_important_bow", weight: 1 },
    ],
    outfit: [
        { emote: "animations/emotes/ta_da", weight: 5 },
        { emote: "animations/emotes/very_important_bow", weight: 2 },
    ],
    item: [
        { emote: "animations/emotes/look_what_i_earned", weight: 5 },
        { emote: "animations/emotes/ta_da", weight: 1 },
    ],
    ambient: [
        { emote: "animations/emotes/buffering_bot", weight: 2 },
        { emote: "animations/emotes/who_me", weight: 1 },
    ],
} as const satisfies Record<GnomeEmoteOccasion, readonly WeightedEmote[]>;

export interface GnomeEmoteHistory {
    readonly recent: readonly GnomeSpineEmote[];
    readonly lastByOccasion: Readonly<
        Partial<Record<GnomeEmoteOccasion, GnomeSpineEmote>>
    >;
}

export function createEmptyGnomeEmoteHistory(): GnomeEmoteHistory {
    return { recent: [], lastByOccasion: {} };
}

/** Avoid the last response to this occasion and the last clip across occasions. */
export function selectGnomeEmote(
    occasion: GnomeEmoteOccasion,
    history: Readonly<GnomeEmoteHistory> = createEmptyGnomeEmoteHistory(),
    random: () => number = Math.random,
): { emote: GnomeSpineEmote; history: GnomeEmoteHistory } {
    const pool: readonly WeightedEmote[] = GNOME_EMOTE_POOLS[occasion];
    const last = history.recent[history.recent.length - 1];
    const lastForOccasion = history.lastByOccasion[occasion];
    const fresh = pool.filter(
        ({ emote }) => emote !== last && emote !== lastForOccasion,
    );
    // A two-clip pool can have different last-global and last-occasion clips.
    // In that case, preserving the immediate global no-repeat takes priority.
    const eligible =
        fresh.length > 0 ? fresh : pool.filter(({ emote }) => emote !== last);
    const weighted = eligible.map((candidate) => {
        const recentIndex = history.recent.lastIndexOf(candidate.emote);
        const distance =
            recentIndex < 0 ? 8 : history.recent.length - recentIndex;
        return {
            ...candidate,
            weight: candidate.weight * Math.min(1, distance / 6),
        };
    });
    const total = weighted.reduce((sum, { weight }) => sum + weight, 0);
    const sample = random();
    let roll =
        (Number.isFinite(sample)
            ? Math.min(Math.max(sample, 0), 1 - Number.EPSILON)
            : 0) * total;
    let selected: GnomeSpineEmote | undefined;
    for (const candidate of weighted) {
        roll -= candidate.weight;
        if (roll < 0) {
            selected = candidate.emote;
            break;
        }
    }
    selected ??= weighted[weighted.length - 1]?.emote;
    if (selected === undefined)
        throw new Error(`No Gnome emotes for ${occasion}`);
    return {
        emote: selected,
        history: {
            recent: [...history.recent, selected].slice(-8),
            lastByOccasion: { ...history.lastByOccasion, [occasion]: selected },
        },
    };
}

/** Exact authored clip lengths, used to keep reaction priority through the exit. */
export const GNOME_EMOTE_DURATIONS = {
    "animations/emotes/awkward": 2,
    "animations/emotes/balance_panic": 3.4,
    "animations/emotes/buffering_bot": 4,
    "animations/emotes/disco_gnome": 4,
    "animations/emotes/happy_hop": 1.566667,
    "animations/emotes/heel_click_hooray": 3,
    "animations/emotes/look_what_i_earned": 2,
    "animations/emotes/mastered_it": 2.4,
    "animations/emotes/me_promoted": 4.8,
    "animations/emotes/nailed_it": 1.2,
    "animations/emotes/onwards": 1.8,
    "animations/emotes/overeager_hello": 3,
    "animations/emotes/pebbler_groove": 4,
    "animations/emotes/sideflip_jump": 3,
    "animations/emotes/ta_da": 3,
    "animations/emotes/tiny_tantrum": 3,
    "animations/emotes/very_important_bow": 3.4,
    "animations/emotes/victory_pump": 2.1,
    "animations/emotes/who_me": 2.5,
    "animations/job_complete/job_complete": 4.066667,
} as const satisfies Record<GnomeSpineEmote, number>;
